<template>
    <div id="app-view">
        <BaseAppLayout>
            <template v-slot:top>
                <div class="d-flex align-center px-3 py-3">
                    <AccountMenu />
                    <WhatsNewButton class="ml-2" />
                    <UpgradePlanNotification class="ml-auto" />
                </div>

                <MediaControls v-if="!isGuest"/>
            </template>

            <template v-slot:default>
                <splitpanes ref="splitPanes" :dbl-click-splitter="false" class="default-theme align-stretch"
                            @resize="paneResized"
                            @splitter-click="splitterClicked($event)"
                >
                    <pane id="main-layout-left-pane" ref="left" :min-size="defaultSidebarLeftSize" :size="paneLeft">
                        <AppLayoutLeftColumn />
                    </pane>
                    <pane id="main-layout-middle-pane" ref="middle" :size="paneMiddle">
                        <AppLayoutMainArea />
                    </pane>
                    <pane id="main-layout-right-pane" ref="right" :min-size="defaultSidebarRightSize" :size="paneRight">
                        <AppLayoutRightColumn />
                    </pane>
                </splitpanes>
            </template>
        </BaseAppLayout>

        <Templates />
        <Modals />
    </div>
</template>

<script>
import "@/services/indexedDB.service.js";

import BaseAppLayout from '@/layouts/BaseAppLayout.vue';
import AppLayoutLeftColumn from '@/components/app/ui/layout/AppLayoutLeftColumn.vue';
import AppLayoutMainArea from '@/components/app/ui/layout/AppLayoutMainArea.vue';
import AppLayoutRightColumn from '@/components/app/ui/layout/AppLayoutRightColumn.vue';
import UpgradePlanNotification from '@/components/app/ui/UpgradePlanNotification.vue';

import Templates from "@/components/app/ui/Templates";
import Modals from "@/components/app/ui/Modals";

import LoadScripts from "@/mixins/LoadScripts";

import { detectSafari } from "@/assets/js/Utilities";
import Cookies from 'js-cookie';

import { Pane, Splitpanes } from 'splitpanes'
import 'splitpanes/dist/splitpanes.css'

import WhatsNewButton from "@/components/app/buttons/WhatsNew.vue";
import AccountMenu from "@/components/account-menu/Menu.vue";
import MediaControls from "@/components/app/ui/MediaControls.vue";

import { mapGetters, mapState } from "vuex";

export default {
    mixins: [LoadScripts],
    components: {
        Templates,
        Modals,

        Splitpanes,
        Pane,

        MediaControls,
        AccountMenu,
        WhatsNewButton,
        BaseAppLayout,
        AppLayoutLeftColumn,
        AppLayoutMainArea,
        AppLayoutRightColumn,
        UpgradePlanNotification
    },

    data() {
        return {
            paneLeft: null,
            paneRight: null,
            paneMiddle: null,
            lastClick: Date.now()
        }
    },

    computed: {
        ...mapGetters('User', ['isGuest']),

        defaultSidebarLeftSize() {
            if (window.innerWidth > 1920) return 17;
            else return 21
        },

        defaultSidebarRightSize() {
            if (window.innerWidth > 1920) return 17;
            else return 21
        },
    },

    created() {
        this.paneLeft = this.defaultSidebarLeftSize;
        this.paneRight = this.defaultSidebarRightSize;
        this.paneMiddle = 100 - this.paneLeft - this.paneRight;
    },

    async mounted() {
        import("@/assets/js/Boot");
        await this.$store.dispatch('Organizations/fetch');

        this.$nextTick(() => {
            setTimeout(() => {
                UI.startApp();
            }, 1000)
        })

        await this.loadScripts();
        this.$store.dispatch("App/setScriptsLoaded");
        this.$store.dispatch("App/setIDTList", window.config.IDT);
        this.$store.dispatch("App/setODTList", window.config.ODT);

        this.detectMobile();

        this.$nextTick(() => {
            setTimeout(() => {
                if (!gpu.renderer.capabilities.isWebGL2) {
                    this.$snackbar('Your browser doesn\'t seem to support WebGL2 which is required for fylm.ai. Please upgrade to a modern browser.', 'error', {
                        class: 'browser-notice',
                        timeout: -1
                    })

                    return
                }
            }, 1000)
        })

        if (detectSafari()) {
            if (Cookies.get('SafariPrivateModeInfoRead')) return;

            this.$snackbar(`Safari is not compatible with fylm.ai. Please use Chrome or Firefox.`, "error", {
                class: 'browser-notice',
                timeout: -1,
                clickHandler() {
                    Cookies.set('SafariPrivateModeInfoRead', true);
                }
            });
        }
    },

    methods: {
        paneResized() {
            window.UI.paneResized();
        },

        splitterClicked($event) {
            let timeClicked = Date.now();
            if (timeClicked - this.lastClick > 250) {
                this.lastClick = timeClicked;
                return;
            }

            if ($event.index == 1) {
                let rightPaneSize = this.$refs.right._data.style.width.split('%')[0];
                this.$refs.splitPanes.panes[0].size = this.defaultSidebarLeftSize;
                this.$refs.splitPanes.panes[1].size = 100 - this.defaultSidebarLeftSize - parseFloat(rightPaneSize);
            }

            if ($event.index == 2) {
                let leftPaneSize = this.$refs.left._data.style.width.split('%')[0];
                this.$refs.splitPanes.panes[2].size = this.defaultSidebarRightSize;
                this.$refs.splitPanes.panes[1].size = 100 - this.defaultSidebarRightSize - parseFloat(leftPaneSize);
            }

            setTimeout(() => {
                window.UI.paneResized();
            }, 200)
        },

        detectMobile() {
            if (
                navigator.userAgent.match(/Android/i) ||
                navigator.userAgent.match(/webOS/i) ||
                navigator.userAgent.match(/iPhone/i) ||
                navigator.userAgent.match(/iPad/i) ||
                navigator.userAgent.match(/iPod/i) ||
                navigator.userAgent.match(/BlackBerry/i) ||
                navigator.userAgent.match(/Windows Phone/i)
            ) {
                this.$snackbar(`At this stage fylm.ai works on desktop only`, "error", {
                    timeout: -1,
                });
            }
        }
    }

};
</script>

<style lang="scss">
@import "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.12.1/css/all.min.css";
@import "~zurb-twentytwenty/css/twentytwenty-no-compass.css";
@import "~zurb-twentytwenty/css/twentytwenty.css";
@import "~jquery-ui-bundle/jquery-ui.min.css";
@import "~round-slider/dist/roundslider.min.css";
@import "@/assets/scss/main.scss";

#app-view {
    color: #989898 !important;

    .splitpanes__splitter {
        border-left: initial;
        background: transparent;
        //width: 20px;
        z-index: 30;
    }

    #main-layout-left-pane,
    #main-layout-right-pane {
        height: initial;
        background-color: var(--color-bg-dark);;
        overflow: visible;
    }

    #main-layout-left-pane {
        border-radius: 0 var(--box-rounding) var(--box-rounding) 0;
    }

    #main-layout-right-pane {
        border-radius: var(--box-rounding) 0 0 var(--box-rounding);
    }

    #main-layout-left-pane + .splitpanes__splitter {
        margin-left: -1px;
    }

    #main-layout-middle-pane + .splitpanes__splitter {
        margin-right: -3px;
        margin-left: initial;
        &:before {
            right: 0;
            left: initial;
            transform: translate(-50%, -50%);
        }
    }

    .splitpanes__pane {
        background-color: transparent;
    }

    .splitpanes__splitter {
        &:before {
            display: none;
            //height: 15px;
            //width: 4px;
            //border-left: 1px solid rgb(135, 135, 135);
            //border-right: 1px solid rgb(135, 135, 135);
        }

        &:after {
            display: none;
        }
    }

    //&.init {
    //    .splitpanes__splitter {
    //        display: none;
    //    }
    //}

    .template {
        display: none;
    }

    .disabled {
        cursor: not-allowed !important;
        opacity: 0.3;
    }
}

.file-tags-menu-container {
    border-radius: var(--box-rounding);
    min-width: initial !important;

    &.--rating {
        .v-list-item {
            display: flex !important;
            align-items: center;
        }

        .v-list-item__title {
            margin-left: 0 !important;
        }
    }

    .v-list {
        background: var(--dark-gray);
        padding: 25px 0;
        position: relative;
        display: flex;
        flex-direction: column;

        .v-list-item {
            min-height: 20px;
            padding: 0 25px;
            display: grid;
            grid-template-columns: 18px 1fr 18px;
            cursor: pointer;

            &--link:before {
                display: none !important;
            }

            &.--first {
                order: -1;
            }

            &:hover {
                background: rgb(54, 54, 54);
            }

            &.full-span {
                grid-template-columns: 1fr;
            }

            &.stars-icons-container {
                grid-template-columns: repeat(5, 1fr);
            }

            &[aria-expanded="true"] {
                background: rgb(54, 54, 54);
            }

            &.--selected {
                .material-icons,
                .v-list-item__title {
                    color: var(--primary);
                }
            }

            &.v-list-item--disabled {
                opacity: 0.6;
            }

            .material-icons {
                color: #989898;
                font-size: 18px;
                width: 18px;
            }

            .v-list-item__title {
                margin-left: 4px;
                color: #989898;
                font-size: 1rem;
            }

            &:after {
                display: none;
            }
        }
    }
}
</style>
